import { useState } from 'react';
import type { PlacementRelativePitchComparison } from '../../shared/placement';

type Props = {
  learnerPitchHz: (number | null)[]; referencePitchHz: (number | null)[]; label: string;
  relativePitchComparison?: PlacementRelativePitchComparison;
};

export default function PlacementPitchComparison({ learnerPitchHz, referencePitchHz, label, relativePitchComparison }: Props) {
  const [showRaw, setShowRaw] = useState(false);
  const relative = Boolean(relativePitchComparison && !showRaw);
  const learner = relative ? relativePitchComparison!.learnerSemitones : learnerPitchHz;
  const reference = relative ? relativePitchComparison!.referenceSemitones : referencePitchHz;
  const points = [...learner, ...reference].filter((value): value is number => value !== null && Number.isFinite(value));
  if (points.length < 2) return <p>有聲片段不足，未能畫出音高對照。Not enough voiced pitch evidence to plot.</p>;

  // Both curves share the stored utterance registers. Never recenter each word.
  const minimum = Math.min(...points, relative ? 0 : Infinity), maximum = Math.max(...points, relative ? 0 : -Infinity);
  const span = Math.max(relative ? 4 : 20, maximum - minimum), middle = (minimum + maximum) / 2;
  const low = middle - span * 0.6, high = middle + span * 0.6;
  const y = (value: number) => 114 - (value - low) / (high - low) * 98;
  const tickLabel = (value: number) => relative ? `${Math.abs(value) < 0.05 ? '0.0' : value.toFixed(1)}` : `${Math.round(value)}`;
  function curve(values: (number | null)[]) {
    let drawing = false;
    return values.map((value, index) => {
      if (value === null || !Number.isFinite(value)) { drawing = false; return ''; }
      const point = `${43 + index / Math.max(1, values.length - 1) * 294},${y(value)}`;
      const segment = `${drawing ? 'L' : 'M'}${point}`; drawing = true; return segment;
    }).join(' ');
  }
  return <figure className="cnt-placement-pitch">
    {relativePitchComparison && <div className="cnt-pitch-modes" role="group" aria-label="音高顯示 Pitch display">
      <button type="button" aria-pressed={relative} onClick={() => setShowRaw(false)}>相對音高 <small>Relative pitch</small></button>
      <button type="button" aria-pressed={!relative} onClick={() => setShowRaw(true)}>原始基頻 <small>Raw Hz</small></button>
    </div>}
    <svg viewBox="0 0 350 145" role="img" aria-label={`${label} ${relative ? '相對音高（半音）Relative pitch in semitones' : '原始基頻（Hz）Raw pitch in Hz'}`}>
      {[high, middle, low].map((value, index) => <g key={index} className="cnt-pitch-axis"><line x1="40" x2="340" y1={y(value)} y2={y(value)} /><text x="34" y={y(value) + 4} textAnchor="end">{tickLabel(value)}</text></g>)}
      {relative && low < 0 && high > 0 && <line className="cnt-pitch-zero" x1="40" x2="340" y1={y(0)} y2={y(0)} />}
      <path className="reference" d={curve(reference)} /><path className="learner" d={curve(learner)} />
      <text className="cnt-pitch-axis-label" x="43" y="135">配對位置 · Aligned positions</text>
    </svg>
    <figcaption><span className="reference">示範 Reference · 虛線</span><span className="learner">錄音 Recording · 實線</span>
      <small>{label} · {relative ? '半音 Semitones。已調整成段錄音嘅聲線高低，保留各字嘅相對音高差異，方便比較聲調升降；毋須模仿示範聲線。' : `基頻 F0 · ${Math.round(minimum)}–${Math.round(maximum)} Hz。聲線音域會令曲線分開，毋須模仿示範聲線。`}</small>
      <small>橫軸係配對位置，唔係秒數；空隙代表未有穩定音高。呢個圖唔代表字音正確率或已辨認實際聲調類別。</small>
    </figcaption>
  </figure>;
}
